import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatInput, MatInputModule } from '@angular/material/input';
import { forkJoin, map, of, Subscription, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { FileDescriptor, FileUploadModule } from '@copo/file-upload-new';

import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { FastLaneService } from '../../../services/fastlane.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { FastLaneWizardStep } from '../../../models/fastlane-wizard-state';
import {
	currencyList,
	SHIP_TO_CUSTOM
} from '../../../models/fastlane-list';
import { FormFastLane } from '../../../models/form-fastlane';
import { FastLaneWizardService } from '../../../services/fastlane-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { UserService } from '../../../../shared/services/user.service';
import { safe } from '../../../../shared/utils/safe';
import { TranslationService } from '../../../../shared/services/translation.service';
import { FastLaneWizardStepperService } from '../../../services/fastlane-wizard-stepper.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { FastLaneRequestAttachment, FastLaneRequestAttachmentForm }
	from '../../../models/fastlane.model';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';
import { AttachmentService } from '../../../services/fastlane-attachment-service';

const DEFAULT_CURRENCY_EURO = 'EUR';

@Component({
	selector: 'copo-fastlane-step-header',
	templateUrl: './step-header.component.html',
	styleUrl: './step-header.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatTooltipModule,

		MatButtonModule,
		FileUploadModule,
		SortOptionsPipe
	]
})

export class StepHeaderComponent extends WizardPageContent implements OnDestroy, OnInit {
	@ViewChild('coNbrField', { read: ElementRef }) coNbrField!: ElementRef<MatInput>;

	isLoading = true;
	isSoldToAvailable = true;
	userBocData?: SoldToRelationshipGroup[];
	readonly form: FormFastLane;
	isSoldTo = true;
	isShipTo = true;
	bocs?: Map<string, BocClient>;
	soldToBocList?: BocClient[];
	description?: string;
	soldToList?: string[];
	sub: Subscription | undefined;
	uploadFileCountSub: Subscription | undefined;
	endUser!: string;
	customer?: Subscription;
	isCustomerInfoSelected = false;
	configForBoc = { codeProp: 'businessId', valueProp: 'name' };
	configForCurrency = { codeProp: 'code', valueProp: 'description' };
	private readonly VIEW_STEP_NUM = 0;
	private fileUploadCount = 0;

	constructor(
		private readonly fastlaneWizardService: FastLaneWizardService,
		private readonly fastlaneService: FastLaneService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly fastlaneStepperService: FastLaneWizardStepperService,
		public fb: FormBuilder,
		private readonly attachmentService: AttachmentService
	) {
		super(FastLaneWizardStep.MAINDATA, fastlaneWizardService.getState());
		this.form = this.fastlaneWizardService.getStateFastLaneForm();
		this.description = <string> this.form.getFormDescription().value;
		this.loadParameters();
		this.uploadFileCountSub = this.attachmentService.uploadAttachmentObs.subscribe(fileCountCompleted => {
			this.fileUploadCount += fileCountCompleted;
		});
	}

	ngOnInit() {
		this.fastlaneStepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
		if (this.uploadFileCountSub) {
			this.uploadFileCountSub.unsubscribe();
		}
	}

	checkError(): boolean {
		if (this.fileUploadCount > 0) {
			return true;
		}
		if (!this.form.getFormCustomerRequestName().valid
			|| !this.form.getFormSoldTo().valid
			|| !this.form.getFormShipTo().valid
			|| !this.form.getFormShipToCustom().valid
			|| !this.form.getFormShipToPerson().valid
			|| !this.form.getFormShipToContact().valid
			|| !this.form.getFormCurrency().valid) {
			this.form.markAllAsTouched();
			return true;
		}
		return false;
	}

	getCurrencyList() {
		return currencyList;
	}

	changeSoldTo() {
		const soldToBusinessId = <string> this.form.getFormSoldTo().value;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		this.isShipTo = true;
		this.getShipToList();
		if (soldToGroup && bocs && soldToGroup.shipTos.length === 0) {
			this.isShipTo = false;
		}
	}

	compareWithFn(soldTo1: string, soldTo2: string) {
		return soldTo1 === soldTo2;
	}

	changeSoldToValues() {
		const shipToList = this.getShipToList();
		this.fastlaneService.filterShiptoList = shipToList;
		if (shipToList.length === 0) {
			this.form.getFormShipTo().setValue(SHIP_TO_CUSTOM);
		} else if (!shipToList.some(shipto => shipto.businessId === this.form.getFormShipTo().value)) {
			this.form.getFormShipTo().setValue(undefined);
		}
		this.changeBocValues();
	}

	changeBocValues() {
		const newBoc = {
			soldTo: <string | undefined> this.form.getFormSoldTo().value,
			shipTo: <string | undefined> this.form.getFormShipTo().value
		};
		const lastBoc = this.form.getAndUpdateLastBoc(newBoc);
		if (
			(
				lastBoc.soldTo !== undefined
				&& newBoc.soldTo !== undefined
				&& lastBoc.soldTo !== newBoc.soldTo
			)
			|| (
				lastBoc.shipTo !== undefined
				&& newBoc.shipTo !== undefined
				&& lastBoc.shipTo !== newBoc.shipTo
			)
		) {
			this.form.getAndUpdateLastBoc(lastBoc);
			this.form.getFormSoldTo().setValue(lastBoc.soldTo, { emitEvent: false });
			this.form.getFormShipTo().setValue(lastBoc.shipTo, { emitEvent: false });
		}
		if (newBoc.shipTo) {
			this.updateSoldToValidators(newBoc.shipTo);
		}
	}

	updateSoldToValidators(soldToValue: string) {
		if (soldToValue === SHIP_TO_CUSTOM) {
			this.form.getFormShipToCustom().setValidators([Validators.required]);
			this.form.getFormShipToCustom().updateValueAndValidity();
			this.form.getFormShipToPerson().setValidators([Validators.required]);
			this.form.getFormShipToPerson().updateValueAndValidity();
			this.form.getFormShipToContact().updateValueAndValidity();
			this.form.getFormShipToPerson().setValue('N/A');
			this.form.getFormShipToContact().setValue('N/A');
		} else {
			this.form.getFormShipToCustom().setValidators([]);
			this.form.getFormShipToCustom().updateValueAndValidity();
			this.form.getFormShipToPerson().setValidators([]);
			this.form.getFormShipToPerson().updateValueAndValidity();
			this.form.getFormShipToContact().setValidators([]);
			this.form.getFormShipToContact().updateValueAndValidity();
		}
	}

	getShipToList() {
		const soldToBusinessId = <string> this.form.getFormSoldTo().value;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	requestNameSpaceHandle() {
		const coNbrField = this.form.getFormCustomerRequestName();
		const stringValue = <string>coNbrField.value;
		this.form.getFormCustomerRequestName().setValue(stringValue.trim());
	}

	async checkSoldTo() {
		if (!this.isSoldTo) {
			this.isSoldToAvailable = false;
			await this.router.navigate(['/fastlane/wizard'], { replaceUrl: true });
		}
	}

	async addAttachments(events: FileList) {
		const attachmentFormGroups = <FormArray> this.form.get('fastLaneAttachments');
		const attachmentErrorControl = <FormControl> this.form.get('attachmentErrorMessages');
		const files = Array.from(events);
		await this.attachmentService.uploadFiles(files, attachmentFormGroups, attachmentErrorControl);
	}

	removeAttachmentEvent(file: FileDescriptor) {
		const attachmentFormGroups = <FormArray> this.form.get('fastLaneAttachments');
		this.attachmentService.removeAttachment(<FastLaneRequestAttachment>file.data, attachmentFormGroups);
	}

	fileErrors(): string[] {
		const attachmentErrorControl = <FormControl> this.form.get('attachmentErrorMessages');
		return [<string>attachmentErrorControl.value];
	}

	files(): FileDescriptor[] {
		const attachmentFormGroups = <FormArray> this.form.get('fastLaneAttachments');
		const fileDescriptorArray: FileDescriptor[] = [];
		for (const itemRow of attachmentFormGroups.value) {
			const attachmentFormValue = <FastLaneRequestAttachmentForm>itemRow;
			const attachment = attachmentFormValue.fastlaneAttachment;
			const fileDescriptor: FileDescriptor = {
				localFile: attachment.localFile,
				error: Boolean(attachment.error),
				progress: attachment.progress,
				type: attachment.type,
				name: attachment.fileName,
				url: attachment.localUrl,
				data: attachment
			};
			fileDescriptorArray.push(fileDescriptor);
		}
		return fileDescriptorArray;
	}

	private loadParameters() {
		this.sub = this.fastlaneService.getBocs()
			.pipe(switchMap(userBocData => {
				if (userBocData.length === 0) {
					return of(<const>[userBocData, <BocClient[]>[]]);
				}
				const businessIds = Array.from(
					new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers]))
				);
				return forkJoin([
					of(userBocData),
					this.userService.getBocs(
						['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					).pipe(map(v => v.results))
				]);
			})).subscribe({
				next: async ([userBocData, bocs]) => {
					this.userBocData = userBocData;
					this.sub = undefined;
					this.isLoading = false;
					const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
					this.soldToList = this.userBocData.map(v => v.soldTo);
					this.soldToBocList = this.soldToList.map(v => safe(bocsMap.get(v)));
					this.fastlaneService.filteredSoldtoShiptoList = this.soldToBocList;
					this.setupDefaultCurrencyAndSoldTo(this.soldToBocList);
					const errorMsg = this.checkSoldToShipToError();
					await this.checkSoldTo();
					if (errorMsg !== undefined) {
						this.fastlaneWizardService.getState().errorInPage = true;
					}
					if (!this.form.getFormSoldTo().value) {
						this.isCustomerInfoSelected = true;
					}
					this.fastlaneService.filterShiptoList = this.getShipToList();
				},
				error: (err: unknown) => {
					console.error('error loading wizard parameters', err);
					this.sub = undefined;
					this.isLoading = false;
					this.isSoldTo = false;
					this.fastlaneWizardService.getState().errorInPage = true;
					this.fastlaneService.showError(
						this.translationService.getTR('fastlane.wizard.parameters.error')
					);
				},
			});
	}

	private checkSoldToShipToError() {
		if (!this.userBocData || this.userBocData.length === 0) {
			this.isSoldTo = false;
			return this.translationService.getTR('fastlane.error.nosoldto');
		}
		return undefined;
	}

	private setupDefaultCurrencyAndSoldTo(soldToBocList: BocClient[]) {
		const fastLaneUserSettings = this.userService.user.getSettings('fastlane_main');
		if (this.form.getFormCurrency().value === null || !this.form.getFormCurrency().value) {
			this.form.getFormCurrency().setValue(
				fastLaneUserSettings?.value.fastLaneMainContent.currency || DEFAULT_CURRENCY_EURO
			);
		}
		if (this.form.getFormSoldTo().value === null || !this.form.getFormSoldTo().value) {
			if (soldToBocList.length === 1) {
				for (const soldTo of soldToBocList) {
					this.form.getFormSoldTo().setValue(soldTo.businessId);
				}
				this.setDefaultShipTo();
			} else if (fastLaneUserSettings) {
				this.form.getFormSoldTo().setValue(fastLaneUserSettings.value.fastLaneMainContent.soldTo);
			}
		}
	}

	private setDefaultShipTo() {
		const shipToList = this.getShipToList();
		if (shipToList.length === 0) {
			this.form.getFormShipTo().setValue(SHIP_TO_CUSTOM);
		}
	}
}
